import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SEOHead from '@/components/marketplace/SEOHead';
import ListingCard from '@/components/marketplace/ListingCard';
import ReviewsWidget from '@/components/marketplace/ReviewsWidget';
import SmartSlot from '@/components/marketplace/SmartSlot';
import UserAvatar from '@/components/marketplace/UserAvatar';
import { toast } from 'sonner';
import {
  BadgeCheck, Building2, MapPin, Users, Star, Package,
  Heart, ShieldCheck, ArrowRight, Zap, Clock,
} from 'lucide-react';

const StatTile = ({ icon: Icon, label, value }) => (
  <div className='flex items-center gap-3 rounded-lg border p-3 bg-secondary/40'>
    <div className='h-9 w-9 rounded-md bg-background flex items-center justify-center'>
      <Icon className='h-4 w-4 text-primary' />
    </div>
    <div>
      <div className='text-lg font-semibold tabular-nums leading-tight'>{value ?? '—'}</div>
      <div className='text-xs text-muted-foreground'>{label}</div>
    </div>
  </div>
);


/**
 * Format a duration (seconds) as a human-friendly response time.
 *   45      -> "under a minute"
 *   2 min   -> "2 min"
 *   3 hrs   -> "3 hrs"
 *   1 day   -> "1 day"
 * Used by the seller-profile response-time chip.
 */
const formatResponseTime = (seconds) => {
  const s = Number(seconds) || 0;
  if (s < 60) return 'under a minute';
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round(s / 3600)} hrs`;
  return `${Math.round(s / 86400)} day${s >= 172800 ? 's' : ''}`;
};

const SellerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [seller, setSeller] = useState(null);
  const [listings, setListings] = useState([]);
  const [totalListings, setTotalListings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [followBusy, setFollowBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/sellers/${id}`),
      api.get(`/sellers/${id}/listings`, { params: { limit: 8 } }),
    ]).then(([s, l]) => {
      setSeller(s.data);
      setListings(l.data.items || []);
      setTotalListings(l.data.total || 0);
    }).catch(() => toast.error('Seller not found')).finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const toggleFollow = async () => {
    if (!user) return navigate('/login', { state: { from: `/seller/${id}` } });
    if (seller?.id === user.id) return;
    setFollowBusy(true);
    try {
      if (seller.is_following) {
        await api.delete(`/follows/${id}`);
        setSeller({ ...seller, is_following: false, followers_count: Math.max(0, (seller.followers_count || 1) - 1) });
        toast.success('Unfollowed');
      } else {
        await api.post(`/follows/${id}`);
        setSeller({ ...seller, is_following: true, followers_count: (seller.followers_count || 0) + 1 });
        toast.success('Following');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setFollowBusy(false); }
  };

  if (loading) return <div className='indus-container py-16 text-center text-muted-foreground'>Loading seller…</div>;
  if (!seller) return <div className='indus-container py-16 text-center text-muted-foreground'>Seller not found.</div>;

  const isOwner = user && user.id === seller.id;
  const displayName = seller.company_name || seller.name;

  return (
    <div className='indus-container py-6' data-testid='seller-profile-page'>
      <SEOHead
        title={`${displayName} — Verified B2B Seller | IndusFact`}
        description={seller.company_description || `View listings and reviews from ${displayName} on IndusFact.`}
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/seller/${id}` : undefined}
      />
      {/* Header */}
      <Card className='p-6 sm:p-8 mb-6'>
        <div className='flex flex-col md:flex-row items-start gap-6'>
          {seller.profile_photo ? (
            <UserAvatar
              name={displayName}
              photo={seller.profile_photo}
              updatedAt={seller.profile_photo_updated_at}
              size={80}
              className='rounded-xl ring-2 ring-border'
              data-testid='seller-avatar'
            />
          ) : (
            <div className='h-20 w-20 rounded-xl border bg-secondary flex items-center justify-center shrink-0'>
              <Building2 className='h-8 w-8 text-primary' />
            </div>
          )}
          <div className='flex-1 min-w-0'>
            <div className='flex flex-wrap items-center gap-2 mb-1'>
              <h1 className='text-2xl sm:text-3xl font-semibold tracking-tight' data-testid='seller-name'>
                {displayName}
              </h1>
              {seller.is_gst_verified && (
                <span
                  className='inline-flex items-center gap-1 text-[11px] font-medium rounded-full bg-[hsl(var(--verified))]/10 text-[hsl(var(--verified))] px-2 py-0.5'
                  data-testid='seller-gst-badge'
                >
                  <BadgeCheck className='h-3 w-3' /> GST Verified
                </span>
              )}
              {seller.verification_level === 'company_verified' && !seller.is_gst_verified && (
                <span className='inline-flex items-center gap-1 text-[11px] font-medium rounded-full bg-secondary text-foreground px-2 py-0.5'>
                  <ShieldCheck className='h-3 w-3' /> Company verified
                </span>
              )}
            </div>
            {seller.company_name && seller.name && seller.company_name !== seller.name && (
              <div className='text-sm text-muted-foreground'>Contact: {seller.name}</div>
            )}
            <div className='flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground'>
              {(seller.primary_city || seller.primary_state) && (
                <div className='flex items-start gap-1 min-w-0 max-w-full'>
                  <MapPin className='h-3.5 w-3.5 shrink-0 mt-0.5' />
                  <span className='min-w-0 break-words'>
                    {[seller.primary_city, seller.primary_state].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
              {user && seller.full_address && (
                <div className='flex items-start gap-1 min-w-0 max-w-full' data-testid='seller-full-address'>
                  <Building2 className='h-3.5 w-3.5 shrink-0 mt-0.5' />
                  <span className='min-w-0 [overflow-wrap:anywhere]'>{seller.full_address}</span>
                </div>
              )}
              <div>Joined {seller.created_at ? new Date(seller.created_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : ''}</div>
            </div>
            {seller.company_description && (
              <p className='text-sm text-muted-foreground mt-3 max-w-2xl'>{seller.company_description}</p>
            )}
            {/* Improvement: response rate + typical response time chip.
                Silent when we have too few inquiries (<3) to make a
                statistically meaningful signal. */}
            {seller.response_metrics?.sample_size >= 3 && (
              <div className='flex flex-wrap gap-2 mt-3' data-testid='seller-response-metrics'>
                {seller.response_metrics.response_rate_pct != null && (
                  <span
                    className='inline-flex items-center gap-1 rounded-full bg-[hsl(var(--verified))]/10 text-[hsl(var(--verified))] px-2 py-0.5 text-xs font-medium'
                    title='Share of buyer inquiries this seller responded to in the last 90 days'
                    data-testid='seller-response-rate'
                  >
                    <Zap className='h-3 w-3' /> {seller.response_metrics.response_rate_pct}% response rate
                  </span>
                )}
                {seller.response_metrics.median_response_seconds != null && (
                  <span
                    className='inline-flex items-center gap-1 rounded-full bg-secondary text-foreground/80 px-2 py-0.5 text-xs font-medium'
                    title='Typical time to first reply (median over last 90 days)'
                    data-testid='seller-response-time'
                  >
                    <Clock className='h-3 w-3' /> Typically replies in {formatResponseTime(seller.response_metrics.median_response_seconds)}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className='flex flex-col gap-2 sm:min-w-[180px]'>
            {!isOwner && (
              <Button
                onClick={toggleFollow}
                disabled={followBusy}
                variant={seller.is_following ? 'outline' : 'default'}
                data-testid='seller-follow-btn'
                className='w-full'
              >
                <Heart className={`h-4 w-4 mr-1.5 ${seller.is_following ? 'fill-primary text-primary' : ''}`} />
                {seller.is_following ? 'Following' : 'Follow seller'}
              </Button>
            )}
            {isOwner && (
              <Link to='/dashboard/profile'>
                <Button variant='outline' className='w-full'>Edit profile</Button>
              </Link>
            )}
          </div>
        </div>

        <div className='grid grid-cols-2 md:grid-cols-4 gap-3 mt-6'>
          <StatTile icon={Package} label='Active listings' value={seller.active_listings ?? 0} />
          <StatTile icon={Users} label='Followers' value={seller.followers_count ?? 0} />
          <StatTile icon={Star} label='Avg. rating' value={seller.avg_rating != null ? `${seller.avg_rating} / 5` : '—'} />
          <StatTile icon={Star} label='Reviews' value={seller.review_count ?? 0} />
        </div>
      </Card>

      {/* Listings */}
      <div className='mb-8'>
        <div className='flex items-end justify-between mb-3'>
          <h2 className='text-xl font-semibold'>Listings from {displayName}</h2>
          {totalListings > listings.length && (
            <Link to={`/sell?q=${encodeURIComponent(displayName)}`} className='text-sm text-primary hover:underline'>
              View all <ArrowRight className='inline h-3.5 w-3.5' />
            </Link>
          )}
        </div>
        {listings.length === 0 ? (
          <Card className='p-8 text-center text-sm text-muted-foreground'>No active listings yet.</Card>
        ) : (
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' data-testid='seller-listings-grid'>
            {listings.flatMap((l, i) => {
              const nodes = [<ListingCard key={l.id} listing={l} />];
              if (i === 3 && listings.length > 5) {
                nodes.push(
                  <SmartSlot
                    key='seller-slot'
                    placement='listing_grid'
                    excludeIds={listings.map(x => x.id)}
                  />
                );
              }
              return nodes;
            })}
          </div>
        )}
      </div>

      {/* Reviews */}
      <ReviewsWidget sellerId={seller.id} isOwner={isOwner} />
    </div>
  );
};

export default SellerProfile;
